import PropTypes from 'prop-types';
import {useState, useEffect, useRef} from 'react';

export default function VideoMediaItem({item, isActive}) {
    const [hasStarted, setHasStarted] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const iframeRef = useRef(null);

    // Pause video when slide becomes inactive
    useEffect(() => {
        if (!iframeRef.current || !hasStarted || !isActive) {
            if (hasStarted && !isActive) {
                iframeRef.current?.contentWindow?.postMessage(
                    JSON.stringify({event: 'command', func: 'pauseVideo', args: []}),
                    '*'
                );
                setIsPaused(true);
            }
        }
    }, [isActive, hasStarted]);

    // Resume video when user taps on paused video
    const handleResume = () => {
        if (iframeRef.current && isPaused) {
            iframeRef.current.contentWindow.postMessage(
                JSON.stringify({event: 'command', func: 'playVideo', args: []}),
                '*'
            );
            setIsPaused(false);
        }
    };

    // Generate YouTube thumbnail URL from video ID
    const thumbnailUrl = `https://img.youtube.com/vi/${item.videoId}/maxresdefault.jpg`;

    // Generate YouTube embed URL with JS API enabled
    const embedUrl = `https://www.youtube.com/embed/${item.videoId}?autoplay=1&enablejsapi=1`;

    const handlePlay = () => {
        setHasStarted(true);
        setIsPaused(false);
    };

    return (
        <div
            className="aspect-video bg-zinc-800 rounded-xl border border-zinc-700/50 flex items-center justify-center hover:border-amber-500/30 transition-colors overflow-hidden relative">
            {!hasStarted ? (
                // Thumbnail state with play button overlay
                <div
                    className="relative w-full h-full cursor-pointer"
                    onClick={handlePlay}
                >
                    <img
                        src={thumbnailUrl}
                        alt={item.title || 'Video'}
                        className="w-full h-full aspect-video object-cover"
                    />
                    {/* Play button overlay */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <button className="w-16 h-16 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all">
                            <svg
                                className="w-8 h-8 text-white ml-1"
                                fill="currentColor"
                                viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                            </svg>
                        </button>
                    </div>
                </div>
            ) : (
                // YouTube iframe embed (stays in DOM once started)
                <div className="relative w-full h-full">
                    <iframe
                        ref={iframeRef}
                        className="w-full h-full"
                        src={embedUrl}
                        title={item.title || 'YouTube video player'}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                        loading="lazy"
                    />
                    {/* Play overlay when paused */}
                    {isPaused && (
                        <div
                            className="absolute inset-0 flex items-center justify-center cursor-pointer bg-black/30"
                            onClick={handleResume}
                        >
                            <button className="w-16 h-16 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all">
                                <svg
                                    className="w-8 h-8 text-white ml-1"
                                    fill="currentColor"
                                    viewBox="0 0 24 24">
                                    <path d="M8 5v14l11-7z"/>
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

VideoMediaItem.propTypes = {
    item: PropTypes.shape({
        videoId: PropTypes.string.isRequired,
        title: PropTypes.string,
    }).isRequired,
    isActive: PropTypes.bool,
};
